import { useTheme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useMemo } from "react";
import { TimeAggregation } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import { BarCell } from "../BarCell";
import { defaultTableSx } from "../tableFormatting";
import { GroupedScheduleSeries } from "../types";
import { useTranslation } from "react-i18next";

interface ScheduleRow {
  id: number;
  mixGroup: string;
  scheduled_mass: number;
}

interface ScheduleTableProps {
  scheduleSeries: GroupedScheduleSeries;
  selectedTimeAggregation: TimeAggregation;
}

export const ScheduleTable = ({
  scheduleSeries,
  selectedTimeAggregation,
}: ScheduleTableProps) => {
  const { t } = useTranslation();
  const { t: tenantT } = useTenantTranslation();

  const rows = useMemo(
    () => buildRows(scheduleSeries, selectedTimeAggregation),
    [scheduleSeries, selectedTimeAggregation]
  );

  const totalMass = useMemo(
    () => rows.reduce((sum, row) => sum + row.scheduled_mass, 0),
    [rows]
  );

  const totalsRow: ScheduleRow = useMemo(
    () => ({
      id: rows.length,
      mixGroup: tenantT("Total"),
      scheduled_mass: totalMass,
    }),
    [rows.length, totalMass, tenantT]
  );

  const columns = useColumns(totalMass, rows.length, t);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <HotkeyCompliantDataGrid
        sx={defaultTableSx}
        rows={rows}
        pinnedRows={{
          bottom: [totalsRow],
        }}
        columns={columns}
      />
    </div>
  );
};

const buildRows = (
  scheduleSeries: GroupedScheduleSeries,
  selectedTimeAggregation: TimeAggregation
): ScheduleRow[] =>
  scheduleSeries.availableMixGroups.map((mixGroup, i) => {
    const entry = scheduleSeries.items.find(
      ([key]) =>
        key.timeAggregation === selectedTimeAggregation &&
        key.mixGroup === mixGroup
    );
    if (!entry) return { id: i, mixGroup, scheduled_mass: 0 };

    const [, series] = entry;
    const total = series.scheduled_mass.reduce((sum, v) => sum + v, 0);
    return { id: i, mixGroup, scheduled_mass: total };
  });

const useColumns = (
  totalMass: number,
  rowCount: number,
  t: (key: string) => string
): GridColDef<ScheduleRow>[] => {
  const units = useUnitsFormatter(true);
  const theme = useTheme();
  const formatNumber = useFormatNumber();

  return [
    {
      field: "mixGroup",
      headerName: t("mixGroup"),
      flex: 2,
      valueFormatter: (value?: string) => (!value ? "" : t(value)),
    },
    {
      field: "scheduled_mass",
      type: "number",
      headerName: `${t("scheduled")}${units("mass")}`,
      flex: 2,
      align: "right",
      renderCell: (params) => {
        const mass = params.row.scheduled_mass;
        return params.row.id === rowCount ? (
          <div>{formatNumber(0, true)(totalMass)}</div>
        ) : (
          <BarCell
            value={mass}
            formattedValue={formatNumber(0, true)(mass)}
            maxValue={totalMass}
            color={theme.palette.data.blue}
          />
        );
      },
    },
  ];
};
